import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Download, FileText, Trash2, Save, FolderOpen } from "lucide-react";
import SavedEstimatesModal from "./revenue-estimate/SavedEstimatesModal";
import DetailedReport from "./revenue-estimate/detailed/DetailedReport";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

// Seasonal occupancy baseline (avg ≈ 64.5%) — compressed range
const BASE_MONTHLY_OCC = [52, 55, 58, 66, 68, 71, 74, 74, 71, 68, 61, 56];
const BASE_AVG_OCC = BASE_MONTHLY_OCC.reduce((a, b) => a + b, 0) / 12;

// Seasonal nightly rate multipliers relative to base rate (base rate = 100%).
// Months fluctuate above and below the base rate; e.g. Jan = 85%, Aug = 110%.
// Normalized so the twelve multipliers average exactly 1.0 — the nights-weighted
// average nightly rate therefore lands on the Base Rate input (±rounding).
const RATE_MULTIPLIERS = [0.874, 0.899, 0.951, 1.002, 1.054, 1.105, 1.131, 1.131, 1.054, 0.976, 0.925, 0.899];

// Warm-up nightly rate reductions for months 1-3 (positions in ordered sequence)
const WARMUP_REDUCTIONS = [0.50, 0.30, 0.15];

const fmt = (n) => `£${Math.round(n).toLocaleString("en-GB")}`;

const BRANDING_CONFIG = {
  truestays: {
    headerBg: "#fff",
    headerText: "#112215",
    logo: "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/4e392029e_TruestaysLogoDark.png",
    logoAlt: "Truestays",
    logoHeight: 26,
    accent: "#CD533B",
    accentBarLabel: "#fff",
    accentRowText: "#fff",
    accentTextOnWhite: "#CD533B",
    grossRowBg: "#F4F1E7",
    warmupColor: "#5C7268",
    btnBg: "#112215",
    btnText: "#F4F1E7",
    btnHover: "#1A3A2A",
    focusColor: "#CD533B",
    checkboxColor: "#112215",
    iconColor: "#CD533B",
    netRowBg: "#E8F2E0",
    netRowText: "#112215",
    netRowBorder: "#112215",
    chartNetFill: "#1A3A2A",
    chartNetLabel: "#F4F1E7",
    chartMgmtFill: "#D8E9D4",
    chartMgmtLabel: "#112215",
    chartVatFill: "#B8B4A5",
    chartVatLabel: "#fff",
    chartPlatformFill: "#8A9B92",
    chartPlatformLabel: "#fff",
    ink: "#112215",
    headerOnInk: "#F4F1E7",
    netHighlightBg: "#E8F2E0",
    warmupColBgs: ["#FED7C3", "#F3EBE3", "#E8F2E0"],
  },
  houst: {
    headerBg: "#ffe402",
    headerText: "#1A1A1A",
    logo: "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/a1e5abd55_ThumbnailFeaturedImages3.png",
    logoAlt: "Houst",
    logoHeight: 26,
    accent: "#ffe402",
    accentBarLabel: "#1A1A1A",
    accentRowText: "#1A1A1A",
    accentTextOnWhite: "#1A1A1A",
    grossRowBg: "#FFFDE6",
    warmupColor: "#1A1A1A",
    btnBg: "#ffe402",
    btnText: "#1A1A1A",
    btnHover: "#e6cf00",
    focusColor: "#ffe402",
    checkboxColor: "#1A1A1A",
    iconColor: "#ffe402",
    netRowBg: "#ffe402",
    netRowText: "#1A1A1A",
    chartNetFill: "#ffe402",
    chartNetLabel: "#1A1A1A",
    chartMgmtFill: "#1A1A1A",
    chartMgmtLabel: "#fff",
    chartVatFill: "#9A9A9A",
    chartVatLabel: "#fff",
    chartPlatformFill: "#4A4A4A",
    chartPlatformLabel: "#fff",
    ink: "#1A1A1A",
    headerOnInk: "#ffe402",
    netHighlightBg: "#FFFDE6",
    warmupColBgs: ["#FFFDE6", "#FFF6B8", "#FFEC8A"],
  },
};

const labelStyle = {
  fontFamily: "Inter, sans-serif", fontSize: 10, fontWeight: 600,
  letterSpacing: "0.07em", textTransform: "uppercase", color: "#5C7268",
  display: "block", marginBottom: 5,
};

const cellInputSt = {
  border: "1px solid #DDD", borderRadius: 5, padding: "4px 2px",
  fontFamily: "Inter, sans-serif", fontSize: 10, color: "#112215",
  width: "100%", boxSizing: "border-box", outline: "none",
  textAlign: "center", background: "#fff",
};

const cellTextSt = {
  fontFamily: "Inter, sans-serif", fontSize: 10, color: "#112215",
  verticalAlign: "middle", whiteSpace: "nowrap", display: "inline-block", lineHeight: "1.4",
};

// Custom label renderer for chart segments — shows the segment's own (non-cumulative) value
const makeBarLabel = (textColor, dataKey) => (props) => {
  const { x, y, width, height, payload } = props;
  const value = payload?.[dataKey];
  if (!value || height < 18 || width < 20) return null;
  return (
    <text
      x={x + width / 2}
      y={y + height / 2}
      fill={textColor}
      fontSize={9}
      fontFamily="Inter, sans-serif"
      textAnchor="middle"
      dominantBaseline="central"
      fontWeight={600}
    >
      {`£${(value / 1000).toFixed(1)}k`}
    </text>
  );
};

// Custom legend renderer — aligns colour boxes and text vertically
const renderLegend = (props) => {
  const { payload } = props;
  return (
    <div style={{ textAlign: "center", fontSize: 11, fontFamily: "Inter, sans-serif", padding: "8px 0" }}>
      {payload.map((entry, i) => (
        <span key={i} style={{ display: "inline-block", marginRight: 20 }}>
          <span style={{ display: "inline-block", width: 12, height: 12, borderRadius: 2, background: entry.color, verticalAlign: "middle", marginRight: 6 }} />
          <span style={{ color: "#555", verticalAlign: "middle", lineHeight: "12px" }}>{entry.value}</span>
        </span>
      ))}
    </div>
  );
};

export default function RevenueEstimate() {
  const navigate = useNavigate();
  const reportRef = useRef(null);
  const detailedRefs = {
    cover: useRef(null), glance: useRef(null), monthly: useRef(null),
    expect: useRef(null), service: useRef(null),
  };
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [branding, setBranding] = useState("truestays");
  const [exportMode, setExportMode] = useState("basic"); // "basic" | "detailed"

  const [inputs, setInputs] = useState({
    hostName: "", address: "", locality: "Manchester",
    bedrooms: "2", guests: "4",
    baseRate: "150", annualOccupancy: "65",
    commissionRate: "18", vatRate: "20", platformFee: "15.5",
  });
  const [localitySelect, setLocalitySelect] = useState("Manchester");
  const [startMonth, setStartMonth] = useState(0); // 0 = Jan
  const [includeWarmup, setIncludeWarmup] = useState(false);
  const [overrides, setOverrides] = useState({}); // { [pos]: { occ?, nights?, nightlyRate? } }
  const [savedEstimates, setSavedEstimates] = useState([]);
  const [activeEstimateId, setActiveEstimateId] = useState(null);
  const [savingEstimate, setSavingEstimate] = useState(false);
  const [showSavedModal, setShowSavedModal] = useState(false);

  const brand = BRANDING_CONFIG[branding];

  useEffect(() => {
    base44.auth.me().then(user => {
      if (user?.role !== "admin") navigate("/");
      setLoading(false);
    }).catch(() => { navigate("/"); setLoading(false); });
  }, []);

  // Load saved estimates (most recent 30) and prune anything older
  const loadSavedEstimates = async () => {
    try {
      const all = await base44.entities.SavedEstimate.list("-created_date", 100);
      // Prune: keep only the 30 most recent, delete the rest
      if (all.length > 30) {
        const toDelete = all.slice(30);
        if (toDelete.length > 0) {
          await base44.entities.SavedEstimate.deleteMany({ id: { $in: toDelete.map(e => e.id) } });
        }
        setSavedEstimates(all.slice(0, 30));
      } else {
        setSavedEstimates(all);
      }
    } catch (err) {
      console.error("Failed to load saved estimates:", err);
      setSavedEstimates([]);
    }
  };

  useEffect(() => {
    loadSavedEstimates();
  }, []);

  const saveCurrentEstimate = async () => {
    setSavingEstimate(true);
    const payload = {
      hostName: inputs.hostName,
      address: inputs.address || "Untitled",
      locality: inputs.locality,
      bedrooms: inputs.bedrooms,
      guests: inputs.guests,
      baseRate: inputs.baseRate,
      annualOccupancy: inputs.annualOccupancy,
      commissionRate: inputs.commissionRate,
      vatRate: inputs.vatRate,
      platformFee: inputs.platformFee,
      startMonth,
      includeWarmup,
      branding,
      overrides: JSON.stringify(overrides),
    };
    try {
      const created = await base44.entities.SavedEstimate.create(payload);
      await loadSavedEstimates();
      setActiveEstimateId(created.id);
    } catch (err) {
      console.error("Failed to save estimate:", err);
    }
    setSavingEstimate(false);
  };

  const loadEstimate = (est) => {
    setInputs({
      hostName: est.hostName || "",
      address: est.address || "",
      locality: est.locality || "Manchester",
      bedrooms: est.bedrooms || "2",
      guests: est.guests || "4",
      baseRate: est.baseRate || "150",
      annualOccupancy: est.annualOccupancy || "65",
      commissionRate: est.commissionRate || "18",
      vatRate: est.vatRate || "20",
      platformFee: est.platformFee || "15.5",
    });
    setLocalitySelect(est.locality && ["Manchester", "Liverpool", "Stoke-on-Trent"].includes(est.locality) ? est.locality : "Other");
    setStartMonth(est.startMonth ?? 0);
    setIncludeWarmup(est.includeWarmup ?? false);
    setBranding(est.branding || "truestays");
    try {
      setOverrides(est.overrides ? JSON.parse(est.overrides) : {});
    } catch {
      setOverrides({});
    }
    setActiveEstimateId(est.id);
  };

  const deleteEstimate = async (id) => {
    try {
      await base44.entities.SavedEstimate.delete(id);
      if (activeEstimateId === id) setActiveEstimateId(null);
      await loadSavedEstimates();
    } catch (err) {
      console.error("Failed to delete estimate:", err);
    }
  };

  const set = (f) => (e) => setInputs(p => ({ ...p, [f]: e.target.value }));

  const handleLocalityChange = (e) => {
    const val = e.target.value;
    setLocalitySelect(val);
    if (val !== "Other") setInputs(p => ({ ...p, locality: val }));
  };

  const setOverride = (pos, field, value) => {
    setOverrides(p => {
      const current = { ...(p[pos] || {}) };
      current[field] = value;
      if (field === "occ") delete current.nights;
      if (field === "nights") delete current.occ;
      return { ...p, [pos]: current };
    });
  };

  // Build ordered month indices starting from startMonth
  const orderedIndices = [];
  for (let i = 0; i < 12; i++) orderedIndices.push((startMonth + i) % 12);

  const calcData = () => {
    const baseRate = parseFloat(inputs.baseRate) || 0;
    const annualOcc = parseFloat(inputs.annualOccupancy) || 64;
    const commRate = parseFloat(inputs.commissionRate) / 100;
    const vatRate = parseFloat(inputs.vatRate) / 100;
    const platformFee = parseFloat(inputs.platformFee) / 100;
    const scaleFactor = annualOcc / BASE_AVG_OCC;

    const months = orderedIndices.map((origIdx, pos) => {
      const days = DAYS_IN_MONTH[origIdx];
      const computedOcc = Math.min(BASE_MONTHLY_OCC[origIdx] * scaleFactor, 100);

      // Visible nightly rate is always the full (steady-state) rate — warm-up no longer reduces displayed rows
      let computedRate = baseRate * RATE_MULTIPLIERS[origIdx];
      computedRate = Math.round(computedRate);

      const ov = overrides[pos] || {};
      let occ, nights, finalRate, occRaw;

      if (ov.nights !== undefined && ov.nights !== "") {
        nights = parseInt(ov.nights) || 0;
        occRaw = (nights / days) * 100;
        occ = Math.round(occRaw);
      } else if (ov.occ !== undefined && ov.occ !== "") {
        occ = parseFloat(ov.occ) || 0;
        occRaw = occ;
        nights = Math.floor((days * occ) / 100);
      } else {
        occRaw = computedOcc;
        occ = Math.round(occRaw);
        nights = Math.floor((days * occRaw) / 100);
      }

      finalRate = (ov.nightlyRate !== undefined && ov.nightlyRate !== "")
        ? Math.round(parseFloat(ov.nightlyRate) || 0)
        : computedRate;

      // Normal (steady-state) figures — shown in the table rows above net
      const gross = nights * finalRate;
      const mgmtFee = gross * commRate;
      const platFee = gross * platformFee;
      const vat = mgmtFee * vatRate + platFee * vatRate; // VAT on management fee + platform fee
      const net = gross - mgmtFee - vat - platFee;

      // Warm-up adjusted figures for months 1–3 (slashed net + chart + totals)
      let warmupNet = null;
      let chartMgmt = mgmtFee, chartVat = vat, chartPlat = platFee;
      if (includeWarmup && pos < 3) {
        const warmupRate = finalRate * (1 - WARMUP_REDUCTIONS[pos]);
        const wGross = nights * warmupRate;
        const wMgmt = wGross * commRate;
        const wPlat = wGross * platformFee;
        const wVat = wMgmt * vatRate + wPlat * vatRate;
        warmupNet = wGross - wMgmt - wVat - wPlat;
        chartMgmt = wMgmt; chartVat = wVat; chartPlat = wPlat;
      }
      const realNet = warmupNet !== null ? warmupNet : net;

      return { month: MONTHS[origIdx], days, occ, occRaw, nights, nightlyRate: finalRate, gross, mgmtFee, vat, platFee, net, warmupNet, realNet, chartMgmt, chartVat, chartPlat };
    });

    const totals = months.reduce((acc, m) => ({
      nights: acc.nights + m.nights, gross: acc.gross + m.gross,
      mgmtFee: acc.mgmtFee + m.mgmtFee, vat: acc.vat + m.vat,
      platFee: acc.platFee + m.platFee, net: acc.net + m.realNet,
    }), { nights: 0, gross: 0, mgmtFee: 0, vat: 0, platFee: 0, net: 0 });

    const avgNightlyRate = totals.nights > 0 ? Math.round(totals.gross / totals.nights) : 0;
    // Drift-free annual occupancy: weighted by days from un-rounded monthly occupancy
    const avgOcc = Math.round(months.reduce((s, m) => s + m.occRaw * m.days, 0) / 365);

    const chartData = months.map(m => ({
      name: m.month,
      "Net Revenue": Math.round(m.realNet),
      "Mgmt Fee": Math.round(m.chartMgmt),
      "VAT": Math.round(m.chartVat),
      "Platform": Math.round(m.chartPlat),
    }));

    return { months, totals, avgNightlyRate, avgOcc, chartData };
  };

  const data = calcData();

  // Keep the Annual Occupancy input in sync with the computed yearly average
  useEffect(() => {
    const computed = data.avgOcc;
    const current = parseFloat(inputs.annualOccupancy);
    if (!isNaN(current) && computed !== current) {
      setInputs(p => ({ ...p, annualOccupancy: String(computed) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.avgOcc]);

  const handleExportBasicPDF = async () => {
    if (!reportRef.current) return;
    try {
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const margin = 4;
      const availW = pdfW - 2 * margin;
      const availH = pdfH - 2 * margin;

      const canvas = await html2canvas(reportRef.current, {
        scale: 1.5, useCORS: true, backgroundColor: "#ffffff", logging: false,
        windowWidth: 1400,
      });
      const pageRatio = availW / availH;
      const r = canvas.width / canvas.height;
      const targetW = r > pageRatio ? availW : availH * r;
      const imgData = canvas.toDataURL("image/jpeg", 0.85);
      const imgH = targetW * (canvas.height / canvas.width);
      const offsetX = (pdfW - targetW) / 2;
      const offsetY = (pdfH - imgH) / 2;
      pdf.addImage(imgData, "JPEG", offsetX, offsetY, targetW, imgH);

      pdf.save(`Revenue-Estimate-${inputs.hostName || "Report"}.pdf`);
    } catch (err) { console.error(err); }
  };

  const handleExportDetailedPDF = async () => {
    try {
      const order = ["cover", "glance", "monthly", "expect", "service"];
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      for (let i = 0; i < order.length; i++) {
        const node = detailedRefs[order[i]].current;
        if (!node) continue;
        const canvas = await html2canvas(node, { scale: 1.5, useCORS: true, backgroundColor: "#ffffff", logging: false });
        const imgData = canvas.toDataURL("image/jpeg", 0.85);
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, 0, pdfW, pdfH);
      }
      pdf.save(`Revenue-Estimate-${inputs.hostName || "Report"}-Detailed.pdf`);
    } catch (err) { console.error(err); }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    setIsExporting(true);
    // Wait for re-render so inputs become text spans before capture
    await new Promise(r => setTimeout(r, 250));
    if (exportMode === "detailed") {
      await handleExportDetailedPDF();
    } else {
      await handleExportBasicPDF();
    }
    setExporting(false);
    setIsExporting(false);
  };

  if (loading) return (
    <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin" />
    </div>
  );

  // Helper to render editable cell (input) or static text (for PDF export)
  const renderCell = (pos, field, value, minWidth, suffix) => {
    if (isExporting) {
      return <span style={cellTextSt}>{value}{suffix || ""}</span>;
    }
    return (
      <input
        type="number"
        value={value}
        onChange={e => setOverride(pos, field, e.target.value)}
        style={{ ...cellInputSt, minWidth: minWidth || undefined }}
      />
    );
  };

  return (
    <div style={{ background: "#F4F1E7", minHeight: "100vh", paddingTop: 68 }}>
      {/* Page header */}
      <div style={{ background: "#fff", padding: "40px clamp(20px,5vw,80px)", borderBottom: "1px solid #E8E8E8" }}>
        <div style={{ maxWidth: 1300, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
          <div>
            <p style={{ fontFamily: "Inter, sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase", color: "#888", marginBottom: 8, marginTop: 0 }}>Admin Tool</p>
            <h1 style={{ fontFamily: "Inter, sans-serif", fontWeight: 800, fontSize: "clamp(22px,3vw,34px)", color: "#112215", margin: 0, letterSpacing: "-0.02em" }}>Revenue Estimate Generator</h1>
          </div>
          <div className="estimator-actions" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              onClick={() => setShowSavedModal(true)}
              style={{ background: "#fff", color: "#555", border: "1px solid #E5E5E5", borderRadius: 10, padding: "12px 18px", fontFamily: "Inter, sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}
            >
              <FolderOpen size={16} />
              Saved ({savedEstimates.length})
            </button>
            <button
              onClick={saveCurrentEstimate}
              disabled={savingEstimate}
              style={{ background: "#fff", color: "#555", border: "1px solid #E5E5E5", borderRadius: 10, padding: "12px 18px", fontFamily: "Inter, sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, opacity: savingEstimate ? 0.7 : 1 }}
            >
              <Save size={16} />
              {savingEstimate ? "Saving…" : "Save"}
            </button>
            <button
              onClick={handleExportPDF}
              disabled={exporting}
              style={{ background: "#112215", color: "#fff", border: "none", borderRadius: 10, padding: "12px 24px", fontFamily: "Inter, sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, opacity: exporting ? 0.7 : 1 }}
            >
              <Download size={16} />
              {exporting ? "Exporting…" : "Export PDF"}
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1300, margin: "0 auto", padding: "28px clamp(20px,5vw,80px) 80px" }}>
        {/* Input form card */}
        <div className="est-form-card" style={{ background: "#fff", borderRadius: 14, padding: "24px 28px", border: "1px solid #E8E8E8", marginBottom: 28 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
            <FileText size={18} color={brand.iconColor} />
            <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 16, color: "#112215", margin: 0 }}>Property Details</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 14 }}>
            {[
              { key: "hostName", label: "Host Full Name", placeholder: "e.g. John Smith" },
              { key: "address", label: "Property Address", placeholder: "e.g. 12 King Street" },
              { key: "bedrooms", label: "Bedrooms", placeholder: "2" },
              { key: "guests", label: "Max Guests", placeholder: "4" },
              { key: "baseRate", label: "Base Rate (£/night)", placeholder: "150" },
              { key: "annualOccupancy", label: "Annual Occupancy (%)", placeholder: "65" },
              { key: "commissionRate", label: "Management Fee (%)", placeholder: "18" },
              { key: "vatRate", label: "VAT Rate (%)", placeholder: "20" },
              { key: "platformFee", label: "Platform Fee (%)", placeholder: "15.5" },
            ].map(({ key, label, placeholder }) => (
              <div key={key}>
                <label style={labelStyle}>{label}</label>
                <input
                  value={inputs[key]}
                  onChange={set(key)}
                  placeholder={placeholder}
                  style={{ border: "1px solid #E5E5E5", borderRadius: 8, padding: "9px 12px", fontFamily: "Inter, sans-serif", fontSize: 14, color: "#112215", width: "100%", boxSizing: "border-box", outline: "none", transition: "border-color 0.2s" }}
                  onFocus={e => e.target.style.borderColor = brand.focusColor}
                  onBlur={e => e.target.style.borderColor = "#E5E5E5"}
                />
              </div>
            ))}
            {/* Locality dropdown */}
            <div>
              <label style={labelStyle}>Locality / City</label>
              <select
                value={localitySelect}
                onChange={handleLocalityChange}
                style={{ border: "1px solid #E5E5E5", borderRadius: 8, padding: "9px 12px", fontFamily: "Inter, sans-serif", fontSize: 14, color: "#112215", width: "100%", boxSizing: "border-box", outline: "none", cursor: "pointer", transition: "border-color 0.2s" }}
                onFocus={e => e.target.style.borderColor = brand.focusColor}
                onBlur={e => e.target.style.borderColor = "#E5E5E5"}
              >
                <option value="Manchester">Manchester</option>
                <option value="Liverpool">Liverpool</option>
                <option value="Stoke-on-Trent">Stoke-on-Trent</option>
                <option value="Other">Other…</option>
              </select>
            </div>
            {/* Locality free-text — only when "Other" is selected */}
            {localitySelect === "Other" && (
              <div>
                <label style={labelStyle}>Enter Locality</label>
                <input
                  value={inputs.locality}
                  onChange={set("locality")}
                  placeholder="e.g. Leeds"
                  style={{ border: "1px solid #E5E5E5", borderRadius: 8, padding: "9px 12px", fontFamily: "Inter, sans-serif", fontSize: 14, color: "#112215", width: "100%", boxSizing: "border-box", outline: "none", transition: "border-color 0.2s" }}
                  onFocus={e => e.target.style.borderColor = brand.focusColor}
                  onBlur={e => e.target.style.borderColor = "#E5E5E5"}
                />
              </div>
            )}
          </div>

          {/* Branding selector + Start month + warm-up checkbox */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 28, marginTop: 20, alignItems: "flex-end" }}>
            <div>
              <label style={labelStyle}>Report Branding</label>
              <div style={{ display: "flex", border: "1px solid #E5E5E5", borderRadius: 8, overflow: "hidden" }}>
                <button
                  onClick={() => setBranding("truestays")}
                  style={{ padding: "9px 18px", background: branding === "truestays" ? "#112215" : "#fff", color: branding === "truestays" ? "#F4F1E7" : "#555", border: "none", fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.2s" }}
                >
                  Truestays
                </button>
                <button
                  onClick={() => setBranding("houst")}
                  style={{ padding: "9px 18px", background: branding === "houst" ? "#ffe402" : "#fff", color: branding === "houst" ? "#1A1A1A" : "#555", border: "none", fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.2s" }}
                >
                  Houst
                </button>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Start Month</label>
              <select
                value={startMonth}
                onChange={e => setStartMonth(parseInt(e.target.value))}
                style={{ border: "1px solid #E5E5E5", borderRadius: 8, padding: "9px 14px", fontFamily: "Inter, sans-serif", fontSize: 14, color: "#1A1A1A", outline: "none", cursor: "pointer", minWidth: 100 }}
              >
                {MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>PDF Format</label>
              <select
                value={exportMode}
                onChange={e => setExportMode(e.target.value)}
                style={{ border: "1px solid #E5E5E5", borderRadius: 8, padding: "9px 14px", fontFamily: "Inter, sans-serif", fontSize: 14, color: "#1A1A1A", outline: "none", cursor: "pointer", minWidth: 120 }}
              >
                <option value="basic">Basic</option>
                <option value="detailed">Detailed</option>
              </select>
            </div>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", paddingBottom: 10 }}>
              <input
                type="checkbox"
                checked={includeWarmup}
                onChange={e => setIncludeWarmup(e.target.checked)}
                style={{ width: 18, height: 18, cursor: "pointer", accentColor: brand.checkboxColor, marginTop: 2 }}
              />
              <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#444", lineHeight: 1.5 }}>
                Include warm-up period
                <span style={{ display: "block", fontSize: 11, color: "#999" }}>New properties take 2–3 months to build a reputation. Months 1–3 net revenue is shown reduced to reflect the warm-up period.</span>
              </span>
            </label>
          </div>

        </div>

        {/* Report */}
        <div ref={reportRef} className="est-report-card" style={{ background: "#fff", borderRadius: 14, fontFamily: "Inter, sans-serif", overflow: "hidden" }}>

            {/* Header */}
            <div style={{ background: brand.headerBg, padding: "18px 28px", borderBottom: "1px solid #F0F0F0", display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center" }}>
              <div>
                <img
                  src={brand.logo}
                  alt={brand.logoAlt}
                  style={{ height: brand.logoHeight, width: "auto" }}
                />
              </div>
              <div style={{ textAlign: "center" }}>
                <h2 className="!font-body !font-bold" style={{ fontSize: 20, color: brand.headerText, margin: 0, letterSpacing: "-0.02em" }}>Revenue Estimate</h2>
                {includeWarmup && (
                  <p style={{ fontFamily: "Inter, sans-serif", fontSize: 10, color: brand.warmupColor, margin: "4px 0 0", fontStyle: "italic" }}>
                    Warm-up period applied — months 1–3 net revenue shown reduced
                  </p>
                )}
              </div>
              <div style={{ textAlign: "right" }}>
                <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 600, color: brand.headerText }}>{inputs.locality}</span>
                <span style={{ display: "block", fontFamily: "Inter, sans-serif", fontSize: 10, color: brand.warmupColor, marginTop: 2 }}>
                  {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                </span>
              </div>
            </div>
            {branding === "truestays" && <div style={{ height: 3, background: brand.accent }} />}

              {/* Section: Property Info + Chart */}
            <div style={{ padding: "24px 28px", borderBottom: "1px solid #F0F0F0" }}>
              <p style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 11, color: "#5C7268", letterSpacing: "0.08em", textTransform: "uppercase", margin: "0 0 16px" }}>Property Information</p>
              <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 24 }} className="report-two-col">
                {/* Property info */}
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {[
                    ["Host Full Name", inputs.hostName],
                    ["Property Address", inputs.address],
                    ["Locality", inputs.locality],
                    null,
                    ["Bedrooms", inputs.bedrooms],
                    ["Guests", inputs.guests],
                    null,
                    ["Proposed Base Rate", `£${parseFloat(inputs.baseRate || 0).toFixed(2)}`],
                    ["Expected Occupancy", `${inputs.annualOccupancy}%`],
                    null,
                    ["Management Fee Rate", `${inputs.commissionRate}%`],
                    ["VAT Rate", `${inputs.vatRate}%`],
                    ["Platform Fee (Airbnb)", `${inputs.platformFee}%`],
                    null,
                    ["Estimated Gross Revenue", fmt(data.totals.gross)],
                    ["Management Fees", fmt(data.totals.mgmtFee)],
                    ["VAT", fmt(data.totals.vat)],
                    ["Platform Fees", fmt(data.totals.platFee)],
                    ["Estimated Net Revenue", fmt(data.totals.net)],
                    ["Avg Monthly Net Revenue", fmt(data.totals.net / 12)],
                  ].map((row, i) => row === null ? (
                    <div key={i} style={{ height: 6 }} />
                  ) : (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 8, ...((row[0] === "Estimated Net Revenue" || row[0] === "Avg Monthly Net Revenue") ? { background: brand.netHighlightBg, borderRadius: 8, padding: "8px 12px", margin: "2px -12px 2px 0" } : {}) }}>
                      <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: (row[0] === "Estimated Net Revenue" || row[0] === "Avg Monthly Net Revenue") ? brand.ink : "#555", fontWeight: row[0].includes("Estimated") || row[0].includes("Avg Monthly") ? 700 : 400 }}>{row[0]}</span>
                      <span style={{ fontFamily: "Inter, sans-serif", fontSize: row[0] === "Estimated Net Revenue" ? 14 : 12, fontWeight: 700, color: brand.ink, whiteSpace: "nowrap" }}>{row[1]}</span>
                    </div>
                  ))}
                </div>

                {/* Chart */}
                <div style={{ background: "#FAFAFA", borderRadius: 12, padding: "16px 12px" }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data.chartData} margin={{ top: 12, right: 12, left: 0, bottom: 0 }} barSize={34}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEEEEE" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: "Inter, sans-serif" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fontFamily: "Inter, sans-serif" }} axisLine={false} tickLine={false} tickFormatter={v => `£${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={v => `£${v.toLocaleString()}`} contentStyle={{ fontFamily: "Inter, sans-serif", fontSize: 11, borderRadius: 8, border: "1px solid #E5E5E5" }} />
                      <Legend content={renderLegend} />
                      {/* Net Revenue at the bottom of the stack */}
                      <Bar dataKey="Net Revenue" name="Net Revenue" stackId="a" fill={brand.chartNetFill} radius={[0, 0, 4, 4]} label={makeBarLabel(brand.chartNetLabel, "Net Revenue")} />
                      <Bar dataKey="Mgmt Fee" name="Mgmt Fees" stackId="a" fill={brand.chartMgmtFill} label={makeBarLabel(brand.chartMgmtLabel, "Mgmt Fee")} />
                      <Bar dataKey="VAT" name="VAT" stackId="a" fill={brand.chartVatFill} label={makeBarLabel(brand.chartVatLabel, "VAT")} />
                      <Bar dataKey="Platform" name="Platform Fees" stackId="a" fill={brand.chartPlatformFill} radius={[4, 4, 0, 0]} label={makeBarLabel(brand.chartPlatformLabel, "Platform")} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Section: Monthly table */}
            <div style={{ padding: "24px 28px" }}>
              <p style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 11, color: "#5C7268", letterSpacing: "0.08em", textTransform: "uppercase", margin: "0 0 16px" }}>Proposed Monthly Revenue</p>
              <div className="revenue-estimate-table" style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700, fontSize: 10, fontFamily: "Inter, sans-serif", borderBottom: `2px solid ${brand.ink}`, whiteSpace: "nowrap", paddingLeft: 14 }}>Month</th>
                      {data.months.map((m, i) => <th key={i} style={{ padding: "8px 4px", textAlign: "center", fontWeight: 700, fontSize: 10, fontFamily: "Inter, sans-serif", borderBottom: `2px solid ${brand.ink}`, minWidth: 48, ...(includeWarmup && i < 3 ? { background: brand.warmupColBgs[i] } : {}) }}>{m.month}</th>)}
                      <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 700, fontSize: 10, fontFamily: "Inter, sans-serif", borderBottom: `2px solid ${brand.ink}`, background: brand.ink, color: brand.headerOnInk }}>Year</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Occupancy Rate — editable */}
                    <tr>
                      <td style={{ padding: "4px 14px", fontFamily: "Inter, sans-serif", fontSize: 10, color: "#555", borderBottom: "1px solid #F0F0F0", whiteSpace: "nowrap" }}>Occupancy Rate*</td>
                      {data.months.map((m, i) => (
                        <td key={i} style={{ padding: "3px 3px", textAlign: "center", borderBottom: "1px solid #F0F0F0", ...(includeWarmup && i < 3 ? { background: brand.warmupColBgs[i] } : {}) }}>
                          {renderCell(i, "occ", overrides[i]?.occ ?? m.occ, undefined, "%")}
                        </td>
                      ))}
                      <td style={{ padding: "5px 10px", textAlign: "center", fontWeight: 700, fontSize: 10, fontFamily: "Inter, sans-serif", borderBottom: "1px solid #F0F0F0" }}>{data.avgOcc}%</td>
                    </tr>
                    {/* Rented Nights — editable */}
                    <tr style={{ background: "#FAFAFA" }}>
                      <td style={{ padding: "4px 14px", fontFamily: "Inter, sans-serif", fontSize: 10, color: "#555", borderBottom: "1px solid #F0F0F0", whiteSpace: "nowrap" }}>Rented Nights*</td>
                      {data.months.map((m, i) => (
                        <td key={i} style={{ padding: "3px 3px", textAlign: "center", borderBottom: "1px solid #F0F0F0", ...(includeWarmup && i < 3 ? { background: brand.warmupColBgs[i] } : {}) }}>
                          {renderCell(i, "nights", overrides[i]?.nights ?? m.nights)}
                        </td>
                      ))}
                      <td style={{ padding: "5px 10px", textAlign: "center", fontWeight: 700, fontSize: 10, fontFamily: "Inter, sans-serif", borderBottom: "1px solid #F0F0F0" }}>{data.totals.nights}</td>
                    </tr>
                    {/* Avg Nightly Rate — editable */}
                    <tr>
                      <td style={{ padding: "4px 14px", fontFamily: "Inter, sans-serif", fontSize: 10, color: "#555", borderBottom: "1px solid #F0F0F0", whiteSpace: "nowrap" }}>Avg Nightly Rate*</td>
                      {data.months.map((m, i) => (
                        <td key={i} style={{ padding: "3px 3px", textAlign: "center", borderBottom: "1px solid #F0F0F0", ...(includeWarmup && i < 3 ? { background: brand.warmupColBgs[i] } : {}) }}>
                          {renderCell(i, "nightlyRate", overrides[i]?.nightlyRate ?? m.nightlyRate, 56)}
                        </td>
                      ))}
                      <td style={{ padding: "5px 10px", textAlign: "center", fontWeight: 700, fontSize: 10, fontFamily: "Inter, sans-serif", borderBottom: "1px solid #F0F0F0" }}>£{data.avgNightlyRate}</td>
                    </tr>
                    {/* Gross Revenue */}
                    <tr style={{ background: brand.grossRowBg }}>
                      <td style={{ padding: "5px 14px", fontFamily: "Inter, sans-serif", fontSize: 10, fontWeight: 700, color: brand.ink, borderBottom: "1px solid #F0F0F0", whiteSpace: "nowrap" }}>Gross Revenue</td>
                      {data.months.map((m, i) => <td key={i} style={{ padding: "5px 4px", textAlign: "center", fontWeight: 700, fontSize: 10, fontFamily: "Inter, sans-serif", borderBottom: "1px solid #F0F0F0", ...(includeWarmup && i < 3 ? { background: brand.warmupColBgs[i] } : {}) }}>{fmt(m.gross)}</td>)}
                      <td style={{ padding: "5px 10px", textAlign: "center", fontWeight: 700, fontSize: 10, fontFamily: "Inter, sans-serif", borderBottom: "1px solid #F0F0F0" }}>{fmt(data.totals.gross)}</td>
                    </tr>
                    {/* Management Fees */}
                    <tr style={{ background: "#FAFAFA" }}>
                      <td style={{ padding: "5px 14px", fontFamily: "Inter, sans-serif", fontSize: 10, fontStyle: "italic", color: "#666", borderBottom: "1px solid #F0F0F0", whiteSpace: "nowrap" }}>Mgmt Fees ({inputs.commissionRate}%)</td>
                      {data.months.map((m, i) => <td key={i} style={{ padding: "5px 4px", textAlign: "center", fontSize: 10, fontFamily: "Inter, sans-serif", fontStyle: "italic", color: "#666", borderBottom: "1px solid #F0F0F0", ...(includeWarmup && i < 3 ? { background: brand.warmupColBgs[i] } : {}) }}>{fmt(m.mgmtFee)}</td>)}
                      <td style={{ padding: "5px 10px", textAlign: "center", fontWeight: 600, fontSize: 10, fontFamily: "Inter, sans-serif", fontStyle: "italic", color: "#666", borderBottom: "1px solid #F0F0F0" }}>{fmt(data.totals.mgmtFee)}</td>
                    </tr>
                    {/* VAT */}
                    <tr>
                      <td style={{ padding: "5px 14px", fontFamily: "Inter, sans-serif", fontSize: 10, fontStyle: "italic", color: "#666", borderBottom: "1px solid #F0F0F0", whiteSpace: "nowrap" }}>VAT ({inputs.vatRate}%)</td>
                      {data.months.map((m, i) => <td key={i} style={{ padding: "5px 4px", textAlign: "center", fontSize: 10, fontFamily: "Inter, sans-serif", fontStyle: "italic", color: "#666", borderBottom: "1px solid #F0F0F0", ...(includeWarmup && i < 3 ? { background: brand.warmupColBgs[i] } : {}) }}>{fmt(m.vat)}</td>)}
                      <td style={{ padding: "5px 10px", textAlign: "center", fontWeight: 600, fontSize: 10, fontFamily: "Inter, sans-serif", fontStyle: "italic", color: "#666", borderBottom: "1px solid #F0F0F0" }}>{fmt(data.totals.vat)}</td>
                    </tr>
                    {/* Platform Fees */}
                    <tr style={{ background: "#FAFAFA" }}>
                      <td style={{ padding: "5px 14px", fontFamily: "Inter, sans-serif", fontSize: 10, fontStyle: "italic", color: "#666", borderBottom: "1px solid #F0F0F0", whiteSpace: "nowrap" }}>Platform Fees</td>
                      {data.months.map((m, i) => <td key={i} style={{ padding: "5px 4px", textAlign: "center", fontSize: 10, fontFamily: "Inter, sans-serif", fontStyle: "italic", color: "#666", borderBottom: "1px solid #F0F0F0", ...(includeWarmup && i < 3 ? { background: brand.warmupColBgs[i] } : {}) }}>{fmt(m.platFee)}</td>)}
                      <td style={{ padding: "5px 10px", textAlign: "center", fontWeight: 600, fontSize: 10, fontFamily: "Inter, sans-serif", fontStyle: "italic", color: "#666", borderBottom: "1px solid #F0F0F0" }}>{fmt(data.totals.platFee)}</td>
                    </tr>
                    {/* Net Revenue */}
                    <tr style={{ background: brand.netRowBg, borderTop: `2px solid ${brand.ink}` }}>
                      <td style={{ padding: "8px 14px", fontFamily: "Inter, sans-serif", fontSize: 10, color: brand.netRowText, fontWeight: 700, borderBottom: "none", whiteSpace: "nowrap" }}>Net Revenue</td>
                      {data.months.map((m, i) => {
                        const isWarm = includeWarmup && i < 3 && m.warmupNet !== null;
                        return (
                          <td key={i} style={{ padding: "8px 4px", textAlign: "center", fontSize: 10, fontFamily: "Inter, sans-serif", color: brand.netRowText, fontWeight: 700, borderBottom: "none" }}>
                            {isWarm ? (
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.2 }}>
                                <span style={{ position: "relative", display: "inline-block", opacity: 0.6, fontSize: 9, fontWeight: 600, lineHeight: 1.4 }}>
                                  {fmt(m.net)}
                                  <span style={{ position: "absolute", left: -2, right: -2, top: "50%", height: 1, background: brand.netRowText, transform: "translateY(-50%)" }} />
                                </span>
                                <span style={{ fontSize: 10 }}>{fmt(m.warmupNet)}</span>
                              </div>
                            ) : fmt(m.net)}
                          </td>
                        );
                      })}
                      <td style={{ padding: "8px 10px", textAlign: "center", fontSize: 10, fontFamily: "Inter, sans-serif", color: brand.netRowText, fontWeight: 700, borderBottom: "none" }}>{fmt(data.totals.net)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Disclaimer */}
            <div style={{ padding: "14px 28px", background: "#F7F7F7", borderTop: "1px solid #F0F0F0" }}>
              <p style={{ fontFamily: "Inter, sans-serif", fontSize: 9, color: "#999", margin: 0, lineHeight: 1.6, textAlign: "center", fontStyle: "italic" }}>
                {includeWarmup && "Warm-up period applied: months 1–3 net revenue is shown reduced to reflect listing establishment (equivalent to 50%, 30% and 15% nightly rate reductions). "}
                Estimates are based on live market data and the information you've provided, which may be limited. We cannot guarantee any level of performance, as markets fluctuate. Expect lower results in the first 2–3 months while the listing builds traction and reviews — this is known as the warm-up period.
              </p>
            </div>
          </div>

      </div>

      <DetailedReport inputs={inputs} data={data} warmUp={includeWarmup} refs={detailedRefs} />

      {showSavedModal && (
        <SavedEstimatesModal
          savedEstimates={savedEstimates}
          activeEstimateId={activeEstimateId}
          onLoad={loadEstimate}
          onDelete={deleteEstimate}
          onClose={() => setShowSavedModal(false)}
        />
      )}

      <style>{`
        @media (max-width: 700px) { .report-two-col { grid-template-columns: 1fr !important; } }
        @media (max-width: 600px) {
          .estimator-actions { width: 100% !important; }
          .estimator-actions button { flex: 1 !important; justify-content: center !important; padding: 10px 8px !important; font-size: 13px !important; }
          .est-form-card { padding: 16px !important; }
          .est-report-card > div { padding-left: 14px !important; padding-right: 14px !important; }
        }
        .revenue-estimate-table input[type="number"]::-webkit-inner-spin-button,
        .revenue-estimate-table input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        .revenue-estimate-table input[type="number"] { -moz-appearance: textfield; }
        .revenue-estimate-table td { vertical-align: middle !important; white-space: nowrap; }
      `}</style>
    </div>
  );
}